import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";

import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import LinearProgress from "@material-ui/core/LinearProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { isBlank, notBlank } from "../../../util/ext";
import {
  displayAcceptedMimeTypes,
  formatBytes,
  formatTimeRemaining,
  mapUploadFileData,
} from "./uploadUtils";
import { faX } from "@fortawesome/pro-regular-svg-icons";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { includes, lensProp, set, toUpper } from "ramda";
import {
  useRequestUploadUrl,
  useUploadUserFileWithProgress,
} from "./uploadServices";
import { usePrevious } from "../../../common/use-previous";

const useStyles = makeStyles(theme => ({
  bytes: {
    color: "rgba(0, 0, 0, 0.54)",
    paddingLeft: 10,
  },

  cover: {},

  helperText: {
    fontStyle: "italic",
    position: "unset",
    lineHeight: "1.8rem",
  },

  hidden: {
    display: "none",
  },

  inputLabelFileUpload: {
    position: "relative",
    transform: "none",
  },

  selectFileInput: {
    cursor: "pointer",
    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
    color: theme.palette.primary.dark,
    paddingBottom: 5,
  },

  submission: {
    paddingBottom: 25,
  },
}));

const initialProgress = { percent: 0, timeRemaining: 0 };

// fileType: "cover" | "submission"
const FileUpload = ({
  accept,
  id,
  error,
  fileType,
  label,
  maxFileSize,
  required,
  setFileResources,
  showAlert,
  submissionType,
}) => {
  const classes = useStyles();

  const fileInput = useRef(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [progress, setProgress] = useState(initialProgress);

  const requestUploadUrl = useRequestUploadUrl();
  const uploader = useUploadUserFileWithProgress();
  const handleFileChange = async e => {
    //TODO: accept multiple files for submission if adding ALBUM
    const blob = e.currentTarget.files[0];

    // Browsers handle .wav file MIME type inconsistently;
    // force all wav files to be audio/wav
    const mimeType = includes(blob.type, ["audio/x-wav", "audio/wav"])
      ? "audio/wav"
      : blob.type;
    const fileBlob = new File([blob], blob.name, { type: mimeType });
    setFileBlob(fileBlob);
    try {
      if (isBlank(fileBlob)) {
        throw new Error("A file could not be found. Please try again.");
      }

      // max file size in bytes
      if (fileBlob.size > maxFileSize && fileType === "submission") {
        throw new Error(
          `File exceeds ${
            maxFileSize / 10 ** 9
          } GB limit. If you would like to upload a larger file, please email support at support@biblioboard.com`,
        );
      }

      //-- get uploadUrl and fileResource for file
      const fileData = mapUploadFileData(toUpper(fileType), fileBlob);
      const fileUploadResponse = await requestUploadUrl.mutateAsync({
        data: fileData,
      });

      if (notBlank(fileUploadResponse.fileResource)) {
        setFileResources(
          set(lensProp(fileType), fileUploadResponse.fileResource),
        );
      } else {
        throw new Error("An error occurred during upload. Please try again.");
      }

      //-- upload user file to S3
      await uploader.mutateAsync({
        uploadUrl: fileUploadResponse.uploadUrl,
        fileBlob,
        progressEventHandler: e => {
          setProgress({
            percent: Math.floor((e.loaded / e.total) * 100),
            timeRemaining: Math.floor(e.estimated),
          });
        },
      });

      setProgress({ percent: 100, timeRemaining: 0 });
      showAlert("success", ["Your file has uploaded successfully"]);
    } catch (err) {
      if (err.code === "ERR_CANCELED") {
        return;
      } else {
        showAlert("error", [err.message]);
      }
    }
  };

  const handleFileDelete = useCallback(() => {
    if (uploader.isLoading) {
      uploader.abort();
    }

    // reset value attr on input so browser will trigger onChange
    fileInput.current.value = "";

    setFileResources(set(lensProp(fileType), null));
    setFileBlob(null);
    setProgress(initialProgress);
  }, [fileType, setFileResources, uploader]);

  // Manage Reset
  const prevType = usePrevious(submissionType);
  useEffect(() => {
    // if submissionType changes, reset fileBlob
    if (submissionType !== prevType) {
      if (uploader.isLoading) {
        uploader.abort();
      }
      setFileBlob(null);
    }
  }, [submissionType, prevType, uploader]);

  // Cancel any ongoing upload when component unmounts
  useEffect(() => {
    return () => {
      uploader.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fileNameRef = useRef(null);
  return (
    <Grid
      container
      item
      xs={12}
      component={FormControl}
      error={error}
      required={required}
      className={clsx(classes[fileType])}
    >
      <Grid item>
        <InputLabel
          classes={{
            formControl: classes.inputLabelFileUpload,
          }}
        >
          {label}
        </InputLabel>
      </Grid>

      <Grid item container direction="row" xs>
        {isBlank(fileBlob) ? (
          <Grid container alignItems="flex-end" item xs>
            <Grid
              item
              xs
              role="button"
              aria-labelledby={id}
              className={classes.selectFileInput}
              onClick={() => fileInput.current?.click()}
            >
              Select File
            </Grid>
          </Grid>
        ) : (
          <Fragment>
            <Grid item container xs={11}>
              <Grid
                item
                xs={12}
                container
                justify="space-between"
                alignItems="center"
              >
                <Grid ref={fileNameRef} item style={{ maxWidth: 350 }}>
                  {fileNameRef.current &&
                  fileNameRef.current.offsetWidth < 350 ? (
                    <Typography noWrap>{fileBlob.name}</Typography>
                  ) : (
                    <Tooltip title={fileBlob.name}>
                      <Typography noWrap>{fileBlob.name}</Typography>
                    </Tooltip>
                  )}
                </Grid>

                <Grid item>
                  <span className={classes.bytes}>
                    {formatBytes(fileBlob.size)}
                  </span>
                </Grid>

                <Grid item style={{ marginLeft: "auto" }}>
                  <Typography>
                    {notBlank(progress.timeRemaining) &&
                      !isNaN(progress.timeRemaining) &&
                      progress.timeRemaining > 0 &&
                      `${formatTimeRemaining(progress.timeRemaining)} • `}
                    {progress.percent}%
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <LinearProgress
                  variant="determinate"
                  value={progress.percent}
                />
              </Grid>
            </Grid>

            <Grid item xs={1}>
              <IconButton onClick={handleFileDelete}>
                <FontAwesomeIcon icon={faX} />
              </IconButton>
            </Grid>
          </Fragment>
        )}

        <Grid item>
          <input
            ref={fileInput}
            className={classes.hidden}
            type="file"
            accept={accept}
            onChange={handleFileChange}
          />
          <Button
            id={id}
            arial-label="select a file for upload"
            style={{ width: 75 }}
            className={clsx({
              [classes.hidden]: notBlank(fileBlob),
            })}
            disabled={isBlank(submissionType)}
            variant="outlined"
            color="primary"
            onClick={() => fileInput.current?.click()}
          >
            Browse
          </Button>
        </Grid>
      </Grid>

      {maxFileSize && (
        <Grid item>
          <FormHelperText className={classes.helperText}>
            Max file size: {maxFileSize / 10 ** 9} GB
          </FormHelperText>
        </Grid>
      )}

      <Grid item>
        <FormHelperText className={classes.helperText}>
          {displayAcceptedMimeTypes(submissionType)}
        </FormHelperText>
      </Grid>
    </Grid>
  );
};

export default FileUpload;
